import { useState, type FormEvent } from 'react';
import { MUSCLE_LABELS } from '@/engine/balance';
import type { Equipment, Muscle, MovementPattern } from '@/engine/types';
import { Field, inputClass, OptionGroup } from '@/components/ui/Field';
import { createCustomExercise } from './useWorkout';

/**
 * Crear un ejercicio que no está en el catálogo (SPEC 6.D).
 *
 * Se piden los datos que USAN las reglas, no una ficha completa: el patrón y
 * el músculo principal deciden la redundancia y la descompensación, el rango
 * de repeticiones y el incremento deciden la progresión. Sin ellos el
 * ejercicio se registraría pero no participaría en ningún consejo.
 */
const PATTERNS: { value: MovementPattern; label: string }[] = [
  { value: 'empuje_horizontal', label: 'Empuje horizontal' },
  { value: 'empuje_inclinado', label: 'Empuje inclinado' },
  { value: 'empuje_vertical', label: 'Empuje vertical' },
  { value: 'traccion_horizontal', label: 'Tracción horizontal' },
  { value: 'traccion_vertical', label: 'Tracción vertical' },
  { value: 'sentadilla', label: 'Sentadilla' },
  { value: 'bisagra', label: 'Bisagra de cadera' },
  { value: 'zancada', label: 'Zancada' },
  { value: 'aislamiento', label: 'Aislamiento' },
  { value: 'core', label: 'Core' },
];

const EQUIPMENT: { value: Equipment; label: string }[] = [
  { value: 'barra', label: 'Barra' },
  { value: 'mancuernas', label: 'Mancuernas' },
  { value: 'maquina', label: 'Máquina' },
  { value: 'polea', label: 'Polea' },
  { value: 'peso_corporal', label: 'Peso corporal' },
  { value: 'kettlebell', label: 'Kettlebell' },
  { value: 'banda', label: 'Banda' },
  { value: 'disco', label: 'Disco' },
];

const MUSCLES = Object.entries(MUSCLE_LABELS) as [Muscle, string][];

export function CustomExerciseForm({
  onCreated,
  onClose,
}: {
  onCreated: (id: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [pattern, setPattern] = useState<MovementPattern>('aislamiento');
  const [primary, setPrimary] = useState<Muscle>('pectoral');
  const [equipment, setEquipment] = useState<Equipment>('mancuernas');
  const [repMin, setRepMin] = useState('8');
  const [repMax, setRepMax] = useState('12');
  const [incrementKg, setIncrementKg] = useState('2,5');
  const [saving, setSaving] = useState(false);

  const min = Number(repMin);
  const max = Number(repMax);
  const increment = Number(incrementKg.replace(',', '.'));
  const valid =
    name.trim().length > 1 &&
    min >= 1 &&
    max >= min &&
    max <= 50 &&
    increment > 0 &&
    increment < 50;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!valid || saving) return;
    setSaving(true);
    try {
      const id = await createCustomExercise({
        name: name.trim(),
        pattern,
        primary: [primary],
        secondary: [],
        equipment,
        incrementKg: increment,
        repRange: { min, max },
      });
      onCreated(id);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="mt-3 flex flex-col gap-3 overflow-y-auto">
      <Field label="Nombre">
        {(id) => (
          <input
            id={id}
            type="text"
            required
            autoComplete="off"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Press Arnold"
            className={inputClass}
          />
        )}
      </Field>

      <Field label="Patrón de movimiento" hint="Decide con qué otros ejercicios se solapa.">
        {(id) => (
          <select
            id={id}
            value={pattern}
            onChange={(e) => setPattern(e.target.value as MovementPattern)}
            className={inputClass}
          >
            {PATTERNS.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
        )}
      </Field>

      <Field label="Músculo principal">
        {(id) => (
          <select
            id={id}
            value={primary}
            onChange={(e) => setPrimary(e.target.value as Muscle)}
            className={inputClass}
          >
            {MUSCLES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        )}
      </Field>

      <OptionGroup
        label="Material"
        value={equipment}
        options={EQUIPMENT}
        onChange={setEquipment}
        columns={2}
      />

      <div className="grid grid-cols-3 gap-3">
        <Field label="Reps mín.">
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              value={repMin}
              onChange={(e) => setRepMin(e.target.value)}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Reps máx.">
          {(id) => (
            <input
              id={id}
              type="number"
              inputMode="numeric"
              min={1}
              max={50}
              value={repMax}
              onChange={(e) => setRepMax(e.target.value)}
              className={inputClass}
            />
          )}
        </Field>
        <Field label="Salto (kg)" hint="Lo mínimo que puedes subir.">
          {(id) => (
            <input
              id={id}
              type="text"
              inputMode="decimal"
              value={incrementKg}
              onChange={(e) => setIncrementKg(e.target.value)}
              className={inputClass}
            />
          )}
        </Field>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!valid || saving}
          className="tap flex-1 rounded-xl bg-[var(--color-brand-button)] px-4 font-semibold text-[var(--color-on-brand)] disabled:opacity-60"
        >
          Crear y añadir
        </button>
        <button
          type="button"
          onClick={onClose}
          className="tap rounded-xl border border-[var(--color-border)] px-4 text-sm font-medium"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
